<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { Menu, X, Smartphone, Building2, ArrowUpRight } from 'lucide-vue-next'
import logoLlevo from '../../assets/img/logo-llevo-wordmark.png'

const scrolled = ref(false)
const mobileMenuOpen = ref(false)

const navigation = [
  { label: 'Negocios', href: '#negocios' },
  { label: 'Usuarios', href: '#app-usuarios' },
  { label: 'Mandaditos', href: '#mandaditos' },
  { label: 'Cómo funciona', href: '#como-funciona' },
  { label: 'Contacto', href: '#contacto' }
]

const handleScroll = () => {
  scrolled.value = window.scrollY > 24
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

watch(mobileMenuOpen, (isOpen) => {
  document.body.style.overflow = isOpen ? 'hidden' : ''
})

onMounted(() => window.addEventListener('scroll', handleScroll, { passive: true }))
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  document.body.style.overflow = ''
})
</script>

<template>
  <header
    class="fixed inset-x-0 top-0 z-50 transition-all duration-300"
    :class="scrolled || mobileMenuOpen
      ? 'border-b border-slate-200/80 bg-white/95 shadow-[0_8px_30px_rgba(10,17,32,0.06)] backdrop-blur-xl'
      : 'border-b border-transparent bg-white/90 backdrop-blur-md'"
  >
    <nav class="container mx-auto flex items-center justify-between px-6 py-3.5 lg:px-12" aria-label="Navegación principal">
      <!-- Marca -->
      <a href="#inicio" class="flex shrink-0 items-center" aria-label="LLEVO — Inicio">
        <img :src="logoLlevo" alt="LLEVO" class="h-9 w-auto object-contain md:h-10">
      </a>

      <!-- Navegación escritorio -->
      <div class="hidden items-center gap-7 lg:flex">
        <a
          v-for="item in navigation"
          :key="item.href"
          :href="item.href"
          class="text-sm font-semibold text-slate-600 transition-colors duration-200 hover:text-[#c2410c]"
        >
          {{ item.label }}
        </a>
      </div>

      <!-- Acciones escritorio -->
      <div class="hidden items-center gap-2.5 lg:flex">
        <a
          href="#app-usuarios"
          class="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-[#2b4162] transition-colors hover:bg-slate-100"
        >
          <Smartphone :size="16" class="text-[#f0611e]" />
          Descargar app
        </a>
        <router-link
          to="/postular-negocio"
          class="group inline-flex items-center gap-2 rounded-xl bg-[#c2410c] px-5 py-2.5 text-sm font-bold text-white shadow-[0_8px_20px_rgba(240,97,30,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#9a3412] hover:shadow-[0_12px_26px_rgba(240,97,30,0.32)]"
        >
          <Building2 :size="16" />
          Registrar negocio
          <ArrowUpRight :size="15" class="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </router-link>
      </div>

      <!-- Botón móvil -->
      <button
        type="button"
        class="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-[#2b4162] transition-colors hover:bg-slate-50 lg:hidden"
        :aria-expanded="mobileMenuOpen"
        aria-controls="mobile-menu"
        :aria-label="mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'"
        @click="toggleMobileMenu"
      >
        <X v-if="mobileMenuOpen" :size="20" />
        <Menu v-else :size="20" />
      </button>
    </nav>

    <!-- Menú móvil -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-3"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-3"
    >
      <div
        v-if="mobileMenuOpen"
        id="mobile-menu"
        class="border-t border-slate-100 bg-white px-6 pb-8 pt-5 shadow-2xl lg:hidden"
      >
        <div class="flex flex-col gap-1">
          <a
            v-for="item in navigation"
            :key="item.href"
            :href="item.href"
            class="rounded-xl px-3 py-3 text-base font-bold text-[#2b4162] transition-colors hover:bg-slate-50 hover:text-[#c2410c]"
            @click="closeMobileMenu"
          >
            {{ item.label }}
          </a>
        </div>

        <div class="mt-5 grid gap-3 border-t border-slate-100 pt-5">
          <router-link
            to="/postular-negocio"
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-[#c2410c] px-4 py-3 text-center text-sm font-bold text-white shadow-lg shadow-orange-500/20"
            @click="closeMobileMenu"
          >
            <Building2 :size="16" />
            Registrar mi negocio
          </router-link>
          <a
            href="#app-usuarios"
            class="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-center text-sm font-bold text-[#2b4162] transition-colors hover:bg-slate-50"
            @click="closeMobileMenu"
          >
            <Smartphone :size="16" class="text-[#f0611e]" />
            Descargar la app
          </a>
        </div>
      </div>
    </Transition>
  </header>
</template>
